import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { observer } from 'mobx-react-lite';
import { domainStore } from '@/stores/DomainStore';
import { ONE_TIME_AMOUNT_PRESETS_CENTS } from '@/types/billing';
import { isPaddleConfigured, openPaddleOneTimeCheckout } from '@/utils/paddleCheckout';

import './one-time-payment-modal.css';

export type OneTimePaymentKind = 'contribute' | 'donate';
type ModalPhase = 'choose' | 'opening' | 'checkout' | 'thanks';

export interface OneTimePaymentModalProps {
  kind: OneTimePaymentKind;
  isOpen: boolean;
  onClose: () => void;
  /** Required for contribute */
  familyId?: string;
}

function formatUsd(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function successPathForKind(kind: OneTimePaymentKind, familyId?: string): string {
  if (kind === 'contribute') {
    return familyId
      ? `${window.location.origin}/families/${familyId}/overview?billing=contributed`
      : `${window.location.origin}/`;
  }
  return `${window.location.origin}/`;
}

/**
 * Shared one-time Paddle checkout for family contributions and direct donations.
 * Entitlements / balances are never flipped from the client — webhooks only.
 */
const OneTimePaymentModal: React.FC<OneTimePaymentModalProps> = observer(({ kind, isOpen, onClose, familyId }) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const [amountKey, setAmountKey] = useState<string>('1000');
  const [customDollars, setCustomDollars] = useState('');
  const [phase, setPhase] = useState<ModalPhase>('choose');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && phase !== 'opening') {
        onClose();
      }
    },
    [onClose, phase]
  );

  useEffect(() => {
    if (isOpen) {
      setAmountKey('1000');
      setCustomDollars('');
      setPhase('choose');
      setErrorMessage(null);
    }
  }, [isOpen, kind]);

  const resolveAmountCents = (): number | null => {
    if (amountKey === 'custom') {
      const dollars = Number.parseFloat(customDollars);
      if (!Number.isFinite(dollars) || dollars < 1) return null;
      return Math.round(dollars * 100);
    }
    return Number.parseInt(amountKey, 10);
  };

  const handleContinue = async () => {
    setErrorMessage(null);
    if (!isPaddleConfigured()) {
      setErrorMessage(t('oneTimePayment.errors.paddleNotConfigured'));
      return;
    }
    if (kind === 'contribute' && !familyId) {
      setErrorMessage(t('oneTimePayment.errors.familyRequired'));
      return;
    }

    const amountCents = resolveAmountCents();
    if (amountCents == null || amountCents < 100) {
      setErrorMessage(t('oneTimePayment.errors.minAmount'));
      return;
    }

    setPhase('opening');
    try {
      const params =
        kind === 'contribute'
          ? await domainStore.prepareContributeCheckout(familyId as string, amountCents)
          : await domainStore.prepareDonateCheckout(amountCents);

      await openPaddleOneTimeCheckout({
        transactionId: params.transactionId,
        successUrl: successPathForKind(kind, familyId),
        onCompleted: () => setPhase('thanks'),
        onClosed: () => {
          setPhase((current) => (current === 'thanks' ? current : 'choose'));
        }
      });
      setPhase('checkout');
    } catch (err) {
      setPhase('choose');
      setErrorMessage(err instanceof Error ? err.message : t('oneTimePayment.errors.checkoutFailed'));
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
          <Dialog.Content
            className="v01-modal v01-modal--panel one-time-payment-modal"
            aria-labelledby="one-time-payment-title"
            aria-describedby="one-time-payment-desc"
          >
            {phase === 'thanks' ? (
              <>
                <div className="v01-modal__header v01-modal__header--compact">
                  <h2 id="one-time-payment-title" className="v01-modal__title">
                    {t('oneTimePayment.thankYou')}
                  </h2>
                </div>
                <p id="one-time-payment-desc" className="one-time-payment-modal__lede">
                  {t(`oneTimePayment.${kind}.thanks`)}
                </p>
                <div className="one-time-payment-modal__actions">
                  <button type="button" className="btn btn--primary" onClick={onClose}>
                    {tCommon('done')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="v01-modal__header v01-modal__header--compact">
                  <h2 id="one-time-payment-title" className="v01-modal__title">
                    {t(`oneTimePayment.${kind}.title`)}
                  </h2>
                </div>
                <p id="one-time-payment-desc" className="one-time-payment-modal__lede">
                  {t(`oneTimePayment.${kind}.lede`)}
                </p>

                <fieldset
                  className="one-time-payment-modal__fieldset"
                  disabled={phase === 'opening' || phase === 'checkout'}
                >
                  <legend className="one-time-payment-modal__legend">{t('oneTimePayment.amountLegend')}</legend>
                  <RadioGroup.Root
                    className="one-time-payment-modal__radios"
                    value={amountKey}
                    onValueChange={setAmountKey}
                    aria-label={t('oneTimePayment.amountAria')}
                  >
                    {ONE_TIME_AMOUNT_PRESETS_CENTS.map((cents) => (
                      <label key={cents} className="one-time-payment-modal__radio">
                        <RadioGroup.Item value={String(cents)} className="one-time-payment-modal__radio-item" />
                        <span>{formatUsd(cents)}</span>
                      </label>
                    ))}
                    <label className="one-time-payment-modal__radio">
                      <RadioGroup.Item value="custom" className="one-time-payment-modal__radio-item" />
                      <span className="one-time-payment-modal__custom">
                        {t('oneTimePayment.custom')}
                        <input
                          type="number"
                          min={1}
                          step={1}
                          inputMode="decimal"
                          className="one-time-payment-modal__custom-input"
                          placeholder={t('oneTimePayment.customPlaceholder')}
                          value={customDollars}
                          onChange={(e) => {
                            setCustomDollars(e.target.value);
                            setAmountKey('custom');
                          }}
                          onFocus={() => setAmountKey('custom')}
                          disabled={phase === 'opening' || phase === 'checkout'}
                          aria-label={t('oneTimePayment.customAria')}
                        />
                      </span>
                    </label>
                  </RadioGroup.Root>
                </fieldset>

                {phase === 'checkout' ? (
                  <p className="one-time-payment-modal__hint">{t('oneTimePayment.checkoutHint')}</p>
                ) : null}

                {errorMessage ? (
                  <p className="one-time-payment-modal__error" role="alert">
                    {errorMessage}
                  </p>
                ) : null}

                <div className="one-time-payment-modal__actions">
                  <button type="button" className="btn btn--ghost" onClick={onClose} disabled={phase === 'opening'}>
                    {tCommon('cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => void handleContinue()}
                    disabled={phase === 'opening' || phase === 'checkout'}
                  >
                    {phase === 'opening'
                      ? t('actions.openingCheckout')
                      : phase === 'checkout'
                        ? t('actions.checkoutOpen')
                        : t('actions.continueToCheckout')}
                  </button>
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

export default OneTimePaymentModal;
